import {
  AlertTriangle,
  Camera,
  Car,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Phone,
  Shield,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import * as HiIcons from "react-icons/hi";

import AppNavbar from "@/components/common/AppNavbar";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { submitEmergencyReport } from "@/services/apiService";
import { EmergencyContactsService } from "@/services/landing/emergencyContactsService";
import { EmergencyContact } from "@/types/landing";

export default function EmergencyReport() {
  const [emergencyContacts, setEmergencyContacts] = useState<
    EmergencyContact[]
  >([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactSlideIndex, setContactSlideIndex] = useState(0);

  useEffect(() => {
    const fetchEmergencyContacts = async () => {
      try {
        setContactsLoading(true);
        const data = await EmergencyContactsService.getAll();
        // Filter active contacts and sort by order
        const activeContacts = data
          .filter((contact) => contact.isActive)
          .sort((a, b) => a.order - b.order);
        setEmergencyContacts(activeContacts);
      } catch (error) {
        console.error("Failed to load emergency contacts:", error);
        setEmergencyContacts([]);
      } finally {
        setContactsLoading(false);
      }
    };

    fetchEmergencyContacts();
  }, []);
  const [formData, setFormData] = useState({
    attachments: undefined as File[] | undefined,
    contactNumber: "",
    description: "",
    driverName: "",
    emergencyType: "",
    isImmediate: false,
    location: "",
    tripId: "",
    vehiclePlate: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [apiError, setApiError] = useState<null | string>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

const maxFileSize = 4.5 * 1024 * 1024; // 4.5MB

const handleInputChange = (
  e: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
) => {
  const { name, type, value } = e.target;

  if (type === "file") {
    const fileInput = e.target as HTMLInputElement;
    const files = fileInput.files;

    let fileError = "";
    let fileArray: File[] | undefined = undefined;

    if (files && files.length > 0) {
      fileArray = Array.from(files);

      for (const file of fileArray) {
        if (file.size > maxFileSize) {
          fileError = `"${file.name}" is larger than 4.5MB.`;
          break;
        }
      }
    }

    if (fileError) {
      // Clear the actual file input
      fileInput.value = "";

      setErrors((prev) => ({
        ...prev,
        [name]: fileError,
      }));

      setFormData((prev) => ({
        ...prev,
        [name]: undefined,
      }));

      return;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: fileArray,
    }));
  } else if (type === "checkbox") {
    const checkbox = e.target as HTMLInputElement;

    setFormData((prev) => ({
      ...prev,
      [name]: checkbox.checked,
    }));
  } else {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setApiError(null);

    try {
      const result = await submitEmergencyReport(formData);
      if (result.success) {
        setIsSubmitted(true);
      } else {
        setApiError(result.message || "Failed to submit emergency report.");
      }
    } catch (error) {
      let message = "Failed to submit emergency report.";
      if (
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        message = (error as { message: string }).message;
      }
      setApiError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const emergencyTypes = [
    {
      icon: <Car size={20} />,
      label: "Traffic Accident",
      severity: "critical",
      value: "accident",
    },
    {
      icon: <AlertTriangle size={20} />,
      label: "Medical Emergency",
      severity: "critical",
      value: "medical",
    },
    {
      icon: <Shield size={20} />,
      label: "Harassment/Assault",
      severity: "critical",
      value: "harassment",
    },
    {
      icon: <AlertTriangle size={20} />,
      label: "Unsafe Driving",
      severity: "high",
      value: "unsafe-driving",
    },
    {
      icon: <Car size={20} />,
      label: "Vehicle Breakdown",
      severity: "medium",
      value: "vehicle-breakdown",
    },
    {
      icon: <Shield size={20} />,
      label: "Theft/Robbery",
      severity: "critical",
      value: "theft",
    },
    {
      icon: <FileText size={20} />,
      label: "Lost Important Item",
      severity: "low",
      value: "lost-item",
    },
    {
      icon: <AlertTriangle size={20} />,
      label: "Other Emergency",
      severity: "medium",
      value: "other",
    },
  ];

  if (isSubmitted) {
    return (
      <>
        <AppNavbar />
        <div className="min-h-screen bg-base-200 pt-16">
          <Breadcrumbs />
          <div className="flex items-center justify-center px-4 py-16">
            <div className="max-w-lg text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-success/20">
                <CheckCircle className="text-success" size={40} />
              </div>
              <h1 className="mb-4 text-3xl font-bold text-success">
                Emergency Report Submitted!
              </h1>
              <p className="mb-6 text-base-content/70">
                Your emergency report has been received and is being processed
                immediately. Our safety team will contact you shortly.
              </p>
              <div className="space-y-3">
                <div className="alert alert-error">
                  <AlertTriangle size={20} />
                  <div>
                    <h4 className="font-semibold">Immediate Danger?</h4>
                    <p className="text-sm">Call Police or Medical</p>
                  </div>
                </div>
                <div className="alert alert-info">
                  <Phone size={20} />
                  <div>
                    <h4 className="font-semibold">Emergency ID</h4>
                    <p className="text-sm">
                      ER{Math.floor(Math.random() * 100000)}
                    </p>
                  </div>
                </div>
                <div className="alert alert-warning">
                  <Clock size={20} />
                  <div>
                    <h4 className="font-semibold">Response Time</h4>
                    <p className="text-sm">
                      Our team will contact you within 15 minutes
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-6 space-y-2">
                <button
                  className="btn btn-primary w-full"
                  onClick={() => window.location.reload()}
                >
                  Report Another Emergency
                </button>
                <a className="btn btn-error w-full" href="tel:0800424737464">
                  Call Emergency Hotline
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AppNavbar />

      <div className="min-h-screen bg-base-200 pt-16">
        <Breadcrumbs />

        {/* Hero Section */}
        <section className="bg-base-200 py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-7xl px-4">
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-base-300 sm:mb-6 sm:h-20 sm:w-20 lg:h-24 lg:w-24">
                <Zap className="text-warning" size={32} />
              </div>
              <h1 className="mb-3 text-3xl font-bold sm:mb-4 sm:text-4xl md:text-5xl lg:text-6xl">
                Emergency Report
              </h1>
              <p className="mx-auto max-w-2xl text-base opacity-90 sm:text-lg lg:text-xl">
                Report emergencies or urgent safety concerns. Our team responds
                to all emergency reports immediately.
              </p>
            </div>
          </div>
        </section>

        {/* Emergency Contacts */}
        <section className="bg-base-100 py-8 sm:py-12">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-6 text-center text-xl font-bold sm:mb-8 sm:text-2xl">
              Emergency Contacts
            </h2>
            {contactsLoading ? (
              <div className="text-center">
                <span className="loading loading-spinner loading-lg"></span>
                <p className="mt-4">Loading emergency contacts...</p>
              </div>
            ) : emergencyContacts.length > 0 ? (
              <div className="relative">
                {emergencyContacts.length > 4 && (
                  <>
                    <button
                      className="btn btn-circle btn-xs absolute -left-2 top-1/2 z-10 -translate-y-1/2 sm:btn-sm sm:left-0"
                      onClick={() =>
                        setContactSlideIndex((prev) =>
                          prev > 0 ? prev - 1 : emergencyContacts.length - 4,
                        )
                      }
                    >
                      <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                    <button
                      className="btn btn-circle btn-xs absolute -right-2 top-1/2 z-10 -translate-y-1/2 sm:btn-sm sm:right-0"
                      onClick={() =>
                        setContactSlideIndex((prev) =>
                          prev < emergencyContacts.length - 4 ? prev + 1 : 0,
                        )
                      }
                    >
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </>
                )}
                <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
                  {emergencyContacts
                    .slice(
                      emergencyContacts.length > 4 ? contactSlideIndex : 0,
                      emergencyContacts.length > 4
                        ? contactSlideIndex + 4
                        : emergencyContacts.length,
                    )
                    .map((contact) => {
                      // Dynamically resolve icon from contact.icon string (e.g., "HiPhone")
                      const IconComponent =
                        (HiIcons as any)[contact.icon] || HiIcons.HiPhone;

                      return (
                        <div
                          className="card bg-base-200 shadow-lg"
                          key={contact.id}
                        >
                          <div className="card-body p-4 text-center sm:p-6">
                            <IconComponent
                              className={contact.iconColor || "text-primary"}
                              size={24}
                            />
                            <h3 className="card-title justify-center text-base sm:text-lg">
                              {contact.title}
                            </h3>
                            <p
                              className={`mb-2 text-xl font-bold sm:text-2xl ${contact.iconColor || "text-primary"}`}
                            >
                              {contact.phoneNumber}
                            </p>
                            <p className="text-xs text-base-content/70 sm:text-sm">
                              {contact.description}
                            </p>
                            <div className="card-actions mt-3 justify-center sm:mt-4">
                              {contact.phoneNumber.includes("@") ? (
                                <a
                                  className="btn btn-outline btn-xs sm:btn-sm"
                                  href={`mailto:${contact.phoneNumber}`}
                                >
                                  Email Now
                                </a>
                              ) : (
                                <a
                                  className="btn btn-outline btn-xs sm:btn-sm"
                                  href={`tel:${contact.phoneNumber.replace(/\D/g, "")}`}
                                >
                                  Call Now
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {emergencyContacts.length > 4 && (
                  <div className="mt-4 flex justify-center gap-2">
                    {Array.from({
                      length: Math.max(1, emergencyContacts.length - 3),
                    }).map((_, i) => (
                      <button
                        className={`h-2 w-2 rounded-full ${
                          i === contactSlideIndex ? "bg-primary" : "bg-base-300"
                        }`}
                        key={i}
                        onClick={() => setContactSlideIndex(i)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="card bg-base-200 shadow-lg">
                  <div className="card-body p-4 text-center sm:p-6">
                    <Phone className="mx-auto mb-2 text-error" size={20} />
                    <h3 className="card-title justify-center text-base sm:text-lg">
                      Police
                    </h3>
                    <p className="mb-2 text-xl font-bold text-error sm:text-2xl">
                      121
                    </p>
                    <p className="text-xs text-base-content/70 sm:text-sm">
                      For crimes, accidents, and immediate danger
                    </p>
                    <div className="card-actions mt-3 justify-center sm:mt-4">
                      <a
                        className="btn btn-outline btn-xs sm:btn-sm"
                        href="tel:121"
                      >
                        Call Now
                      </a>
                    </div>
                  </div>
                </div>
                <div className="card bg-base-200 shadow-lg">
                  <div className="card-body p-4 text-center sm:p-6">
                    <Phone className="mx-auto mb-2 text-warning" size={20} />
                    <h3 className="card-title justify-center text-base sm:text-lg">
                      Ambulance
                    </h3>
                    <p className="mb-2 text-xl font-bold text-warning sm:text-2xl">
                      10177
                    </p>
                    <p className="text-xs text-base-content/70 sm:text-sm">
                      For medical emergencies
                    </p>
                    <div className="card-actions mt-3 justify-center sm:mt-4">
                      <a
                        className="btn btn-outline btn-xs sm:btn-sm"
                        href="tel:10177"
                      >
                        Call Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Emergency Report Form */}
        <section className="py-8 sm:py-12 lg:py-16">
          <div className="mx-auto max-w-4xl px-4">
            <div className="card bg-base-100 shadow-2xl">
              <div className="card-body p-4 sm:p-6 lg:p-8">
                <div className="mb-4 text-center sm:mb-6">
                  <h2 className="card-title mb-2 justify-center text-2xl sm:text-3xl">
                    Submit Emergency Report
                  </h2>
                  <p className="text-sm text-base-content/70 sm:text-base">
                    Fill out this form for non-immediate emergencies or to
                    document incidents
                  </p>
                </div>

                <form
                  className="space-y-4 sm:space-y-6"
                  onSubmit={handleSubmit}
                >
                  {/* Emergency Type */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-base font-medium sm:text-lg">
                        Type of Emergency
                      </span>
                    </label>
                    <div className="grid gap-2 sm:grid-cols-2 sm:gap-3">
                      {emergencyTypes.map((type) => (
                        <label
                          className={`label cursor-pointer justify-start gap-2 rounded-lg border p-3 hover:bg-base-200 sm:gap-3 sm:p-4 ${
                            type.severity === "critical"
                              ? "border-error"
                              : type.severity === "high"
                                ? "border-warning"
                                : type.severity === "medium"
                                  ? "border-info"
                                  : "border-success"
                          }`}
                          key={type.value}
                        >
                          <input
                            className={`radio radio-sm sm:radio-md ${
                              type.severity === "critical"
                                ? "radio-error"
                                : type.severity === "high"
                                  ? "radio-warning"
                                  : type.severity === "medium"
                                    ? "radio-info"
                                    : "radio-success"
                            }`}
                            name="emergencyType"
                            onChange={handleInputChange}
                            required
                            type="radio"
                            value={type.value}
                          />
                          <div className="flex items-center gap-2">
                            <span className="flex-shrink-0">{type.icon}</span>
                            <span className="label-text text-xs sm:text-sm">
                              {type.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Immediate Emergency Checkbox */}
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-2 rounded-lg border-2 border-error bg-error/10 p-3 sm:gap-3 sm:p-4">
                      <input
                        checked={formData.isImmediate}
                        className="checkbox-error checkbox checkbox-sm sm:checkbox-md"
                        name="isImmediate"
                        onChange={handleInputChange}
                        type="checkbox"
                      />
                      <div>
                        <span className="label-text text-sm font-semibold text-error sm:text-base">
                          This is an immediate emergency requiring urgent
                          response
                        </span>
                        <div className="mt-1 text-xs text-base-content/70 sm:text-sm">
                          Check this if you are in immediate danger or need
                          immediate assistance
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Location Information */}
                  <div className="rounded-lg bg-base-200 p-4 sm:p-6">
                    <h3 className="mb-3 text-lg font-semibold text-primary sm:mb-4 sm:text-xl">
                      Location & Trip Details
                    </h3>

                    <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Current Location
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="location"
                          onChange={handleInputChange}
                          placeholder="Street address or landmark"
                          required
                          type="text"
                          value={formData.location}
                        />
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Trip ID (if applicable)
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="tripId"
                          onChange={handleInputChange}
                          placeholder="e.g., HR123456789"
                          type="text"
                          value={formData.tripId}
                        />
                      </div>
                    </div>

                    <div className="mt-3 grid gap-3 sm:mt-4 sm:gap-4 md:grid-cols-2">
                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Driver Name (if known)
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="driverName"
                          onChange={handleInputChange}
                          placeholder="Driver's name"
                          type="text"
                          value={formData.driverName}
                        />
                      </div>

                      <div className="form-control">
                        <label className="label pb-1">
                          <span className="label-text text-sm font-medium sm:text-base">
                            Vehicle License Plate
                          </span>
                        </label>
                        <input
                          className="input input-sm input-bordered w-full sm:input-md"
                          name="vehiclePlate"
                          onChange={handleInputChange}
                          placeholder="e.g., CA 123-456"
                          type="text"
                          value={formData.vehiclePlate}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-base font-medium sm:text-lg">
                        Emergency Description
                      </span>
                    </label>
                    <textarea
                      className="textarea textarea-bordered textarea-sm h-24 w-full sm:textarea-md sm:h-32"
                      name="description"
                      onChange={handleInputChange}
                      placeholder="Please provide a detailed description of the emergency situation, what happened, when it occurred, and any immediate actions you've taken..."
                      required
                      value={formData.description}
                    ></textarea>
                  </div>

                  {/* Contact Information */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base">
                        Your Contact Number
                      </span>
                    </label>
                    <input
                      className="input input-sm input-bordered w-full sm:input-md"
                      name="contactNumber"
                      onChange={handleInputChange}
                      placeholder="+27 XX XXX XXXX"
                      required
                      type="tel"
                      value={formData.contactNumber}
                    />
                    <div className="label">
                      <span className="label-text-alt text-xs sm:text-sm">
                        We will use this number to contact you immediately about
                        your emergency
                      </span>
                    </div>
                  </div>

                  {/* File Attachments */}
                  <div className="form-control">
                    <label className="label pb-1">
                      <span className="label-text text-sm font-medium sm:text-base">
                        Evidence (Photos/Videos)
                      </span>
                    </label>
                    <input
                      accept="image/*,video/*"
                      className="file-input file-input-bordered file-input-sm w-full sm:file-input-md"
                      multiple
                      name="attachments"
                      onChange={handleInputChange}
                      type="file"
                    />
                    {errors.attachments && (
                      <div className="label">
                        <span className="label-text-alt text-error text-xs sm:text-sm">
                          {errors.attachments}
                        </span>
                      </div>
                    )}
                    <div className="label">
                      <span className="label-text-alt text-xs sm:text-sm">
                        <Camera className="mr-1 inline" size={14} />
                        Upload photos or videos related to the emergency
                        (Optional) maximum 4.5MB
                      </span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="form-control pt-3 sm:pt-4">
                    <button
                      className="btn btn-error btn-sm w-full sm:btn-md lg:btn-lg"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="loading loading-spinner loading-sm"></span>
                          <span className="hidden sm:inline">
                            Submitting Emergency Report...
                          </span>
                          <span className="sm:hidden">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
                          <span className="hidden sm:inline">
                            Submit Emergency Report
                          </span>
                          <span className="sm:hidden">Submit Report</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {apiError && (
                  <div className="alert alert-error mb-4">{apiError}</div>
                )}

                <div className="mt-4 rounded-lg bg-warning/10 p-3 sm:mt-6 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <AlertTriangle className="mt-1 text-warning" size={16} />
                    <div>
                      <h4 className="font-semibold text-warning">
                        Important Notice
                      </h4>
                      <p className="text-sm text-base-content/70">
                        This form is for reporting emergencies to Hareengs
                        safety team. For immediate life-threatening situations,
                        always call local emergency services first (+266 2232 2099 for
                        Police, +266 2224 8000 for Medical).
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Happens Next */}
        <section className="bg-base-100 py-16">
          <div className="mx-auto max-w-7xl px-4">
            <h2 className="mb-12 text-center text-3xl font-bold">
              Emergency Response Process
            </h2>
            <div className="grid gap-8 md:grid-cols-4">
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-error text-xl font-bold text-error-content">
                  1
                </div>
                <h3 className="mb-4 text-xl font-semibold">Immediate Triage</h3>
                <p className="text-base-content/70">
                  Report is immediately classified and prioritized by our safety
                  team
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-warning text-xl font-bold text-warning-content">
                  2
                </div>
                <h3 className="mb-4 text-xl font-semibold">Rapid Response</h3>
                <p className="text-base-content/70">
                  Emergency coordinator contacts you within 15 minutes
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-info text-xl font-bold text-info-content">
                  3
                </div>
                <h3 className="mb-4 text-xl font-semibold">Coordinate Help</h3>
                <p className="text-base-content/70">
                  We coordinate with local authorities and emergency services
                </p>
              </div>

              <div className="text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-success text-xl font-bold text-success-content">
                  4
                </div>
                <h3 className="mb-4 text-xl font-semibold">Follow-up</h3>
                <p className="text-base-content/70">
                  Continuous support until the emergency is resolved
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 24/7 Support */}
        <section className="bg-base-200 py-16">
          <div className="mx-auto max-w-4xl px-4 text-center">
            <h2 className="mb-4 text-3xl font-bold text-primary md:text-4xl">
              24/7 Emergency Support
            </h2>
            <p className="mb-8 text-xl opacity-90">
              Our dedicated safety team is available around the clock to assist
              with emergencies
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              {emergencyContacts.length > 0 && (() => {
                const firstContact = emergencyContacts[0];
                const IconComponent =
                  (HiIcons as any)[firstContact.icon] || HiIcons.HiPhone;
                const isEmail = firstContact.phoneNumber.includes("@");
                return (
                  <a
                    className="btn btn-error btn-lg"
                    href={
                      isEmail
                        ? `mailto:${firstContact.phoneNumber}`
                        : `tel:${firstContact.phoneNumber.replace(/\D/g, "")}`
                    }
                  >
                    <IconComponent size={20} />
                    {isEmail ? "Email Emergency" : "Call Emergency Hotline"}
                    <span className="ml-2">{firstContact.phoneNumber}</span>
                  </a>
                );
              })()}
              {emergencyContacts.length === 0 && (
                <a
                  className="btn btn-error btn-lg"
                  href="tel:0800424737464"
                >
                  <Phone size={20} />
                  Call Emergency Hotline
                </a>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
